import { css } from "@linaria/core";
import cx from "classnames";
import type Highcharts from "highcharts";
import React, { Fragment } from "react";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import ButtonIconV2 from "@ui/ButtonIconV2";
import HighchartsComponent, { type Options } from "@ui/Highcharts";
import { InformationLine } from "@ui/IconV2/react";
import Selector from "@ui/Selector";
import Slider from "@ui/Slider";
import { Tag } from "@ui/Tag";
import Text from "@ui/Text";
import { colors, spacings } from "@ui/Theme";
import TooltipV2 from "@ui/TooltipV2";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { useQuantities } from "../../../../../../api/useQuantities";
import type { Project } from "../../../../../../api/useProjects";
import type { Setup } from "../../../../../../api/useSetups";
import t from "../../../../../../i18n";
import { getFailureColor } from "../../../../useFailureColor";
import {
  clampDifferencePercentage,
  getAvailableQuantitySort,
  getCollinearityLevel,
  getCollinearityPairs,
  getComparableFaults,
  getFailuresByIds,
  getRelevantQuantityIds,
  MAX_DIFFERENCE_PERCENTAGE,
  meetsMinimumImpactDifference,
  MIN_COMPARABLE_FAULTS,
  MIN_DIFFERENCE_PERCENTAGE,
  normalizeFault,
  sortQuantityIds,
  truncateSetupName,
  type CollinearityLevel,
  type QuantitySort,
} from "./faultSignatureComparisonModel";
import ModalV2 from "@ui/ModalV2";

import Field from "@ui/Form/Field";

interface Props {
  onRequestClose: () => void;
  project: Project;
  setups: Setup[];
  initialSetup: Setup;
  selectedFailureIds: string[];
}

const getCollinearityLevelLabel = (level: CollinearityLevel) => {
  switch (level) {
    case "VERY_SIMILAR":
      return t("setup.diagnosis.failure.compare.score.level.very_similar");
    case "SIMILAR":
      return t("setup.diagnosis.failure.compare.score.level.similar");
    case "DISTINCT":
      return t("setup.diagnosis.failure.compare.score.level.distinct");
    default:
      throw new Error(level satisfies never);
  }
};

export const FaultSignatureComparisonModal = ({
  onRequestClose,
  project,
  setups,
  initialSetup,
  selectedFailureIds,
}: Props) => {
  const quantities = useQuantities(project.id);
  const minimumDifferenceLabelId = React.useId();
  const [selectedSetupId, setSelectedSetupId] = React.useState(initialSetup.id);
  const [sort, setSort] = React.useState<QuantitySort>("0");
  const [minimumDifferencePercentage, setMinimumDifferencePercentage] =
    React.useState(MIN_DIFFERENCE_PERCENTAGE);

  const setup = setups.find(candidate => candidate.id === selectedSetupId);
  const selectedFailures = React.useMemo(
    () => getFailuresByIds(initialSetup, selectedFailureIds),
    [initialSetup, selectedFailureIds],
  );
  const selectedFailureNames = React.useMemo(
    () => selectedFailures.map(failure => failure.name),
    [selectedFailures],
  );
  const comparableFaults = React.useMemo(
    () => (setup ? getComparableFaults(setup, selectedFailureNames) : []),
    [selectedFailureNames, setup],
  );
  const normalizedFaults = React.useMemo(
    () => (setup ? comparableFaults.flatMap(fault => normalizeFault(fault, setup) ?? []) : []),
    [comparableFaults, setup],
  );
  const availableSort = React.useMemo(
    () => getAvailableQuantitySort(sort, normalizedFaults.length),
    [normalizedFaults.length, sort],
  );
  const excludedFaults = React.useMemo(
    () =>
      selectedFailures
        .filter(
          selectedFailure =>
            !normalizedFaults.some(
              normalizedFault => normalizedFault.failure.name === selectedFailure.name,
            ),
        )
        .map(failure => failure.displayName),
    [normalizedFaults, selectedFailures],
  );
  const collinearityPairs = React.useMemo(
    () => getCollinearityPairs(normalizedFaults),
    [normalizedFaults],
  );
  const quantityNameById = React.useMemo(
    () => new Map(quantities.map(quantity => [quantity.id, quantity.name])),
    [quantities],
  );
  const sortedQuantityIds = React.useMemo(
    () =>
      sortQuantityIds(
        Array.from(getRelevantQuantityIds(normalizedFaults)),
        normalizedFaults,
        quantityNameById,
        availableSort,
      ),
    [availableSort, normalizedFaults, quantityNameById],
  );
  const quantityIds = React.useMemo(
    () =>
      sortedQuantityIds.filter(quantityId =>
        meetsMinimumImpactDifference(quantityId, normalizedFaults, minimumDifferencePercentage),
      ),
    [minimumDifferencePercentage, normalizedFaults, sortedQuantityIds],
  );
  const chartRef = React.useRef<Highcharts.Chart>(null);
  const chartOptions = React.useMemo<Options>(() => {
    const series: Highcharts.SeriesColumnOptions[] = normalizedFaults.map(fault => ({
      type: "column",
      id: `fault-signature-${fault.failure.id}`,
      name: fault.failure.displayName,
      color: getFailureColor(project, "FAILURE", fault.failure.name),
      data: quantityIds.map(quantityId => ({ y: fault.values.get(quantityId) ?? 0 })),
      replicateInNavigator: false,
    }));
    const initial: Highcharts.Options = {
      chart: { type: "column", fitWrapperHeight: true, fitWrapperWidth: true, marginTop: 5 },
      title: { text: undefined },
      yAxis: { title: { text: t("setup.diagnosis.failure.compare.axis") } },
      tooltip: { shared: true },
      legend: { enabled: true, symbolRadius: 0 },
      plotOptions: { column: { grouping: true } },
      credits: { enabled: false },
    };
    return {
      initial,
      series,
      xAxis: { categories: quantityIds.map(id => quantityNameById.get(id) ?? id) },
    };
  }, [normalizedFaults, project, quantityIds, quantityNameById]);
  const setupOptions = React.useMemo(
    () =>
      [...setups]
        .sort((a, b) => new Date(b.startDate ?? 0).getTime() - new Date(a.startDate ?? 0).getTime())
        .map(candidate => {
          const label = truncateSetupName(candidate.name);
          const candidateFaults = getComparableFaults(candidate, selectedFailureNames);
          const isComparable =
            candidateFaults.length === selectedFailureNames.length &&
            candidateFaults.every(fault => normalizeFault(fault, candidate) !== null);
          return {
            value: candidate.id,
            title: candidate.name,
            tooltipText: label === candidate.name ? undefined : candidate.name,
            label: (
              <span className={cx(selectorOptionLabel, { unavailable: !isComparable })}>
                {label}
              </span>
            ),
          };
        }),
    [selectedFailureNames, setups],
  );
  const sortOptions = React.useMemo(
    () => [
      ...normalizedFaults.map(({ failure }, index) => ({
        value: String(index) as QuantitySort,
        title: failure.displayName,
        tooltipText: failure.displayName,
        label: <span className={sortOptionLabel}>{failure.displayName}</span>,
      })),
      {
        value: "ALPHABETICAL" as const,
        label: t("setup.diagnosis.failure.compare.sort.alphabetical"),
      },
    ],
    [normalizedFaults],
  );
  const handleMinimumDifferenceChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setMinimumDifferencePercentage(clampDifferencePercentage(Number(event.target.value)));
    },
    [],
  );

  return (
    <ModalV2
      className={responsiveComparisonModal}
      isOpen
      onRequestClose={onRequestClose}
      title={t("setup.diagnosis.failure.compare.title")}
      size="m"
    >
      <div className={comparisonContent}>
        <section className={setupSection}>
          <Text type="body1SemiBold">{t("setup.diagnosis.failure.compare.setup")}</Text>
          <Selector
            className={setupSelector}
            options={setupOptions}
            value={selectedSetupId}
            onChange={setSelectedSetupId}
          />
        </section>
        {collinearityPairs.length > 0 && (
          <>
            <div className={sectionSeparator} />
            <section className={scoresSection}>
              <div className={scoresHeading}>
                <Text type="h4">{t("setup.diagnosis.failure.compare.score")}</Text>
                <TooltipV2
                  overlay={
                    <OverlayWrapper text={t("setup.diagnosis.failure.compare.score.tooltip")} />
                  }
                >
                  <ButtonIconV2
                    aria-label={t("setup.diagnosis.failure.compare.score")}
                    className={informationButton}
                  >
                    <InformationLine size="s" />
                  </ButtonIconV2>
                </TooltipV2>
              </div>
              <div className={scoresList}>
                {collinearityPairs.map(({ angle, first, second }) => {
                  const level = getCollinearityLevel(angle);

                  return (
                    <Fragment key={`${first.failure.id}-${second.failure.id}`}>
                      <Text as="div" className={scoreLabel} type="body2">
                        {`${first.failure.displayName} / ${second.failure.displayName}`}
                        <br />
                        <span className={scoreResult}>
                          <Text
                            as="strong"
                            className={cx(scoreValue, level.toLowerCase())}
                            type="body1SemiBold"
                          >
                            {`${angle.toFixed(1)}°`}
                          </Text>
                          <Tag size="s">{getCollinearityLevelLabel(level)}</Tag>
                        </span>
                      </Text>
                    </Fragment>
                  );
                })}
              </div>
            </section>
          </>
        )}
        <div className={sectionSeparator} />

        {excludedFaults.length > 0 && (
          <AdditionnalMessage
            status="INFO"
            text={t("setup.diagnosis.failure.compare.excluded", excludedFaults.join(", "))}
          />
        )}

        {normalizedFaults.length < MIN_COMPARABLE_FAULTS ? (
          <AdditionnalMessage
            status="WARNING"
            text={t("setup.diagnosis.failure.compare.blocked")}
          />
        ) : (
          <>
            <section className={chartSection}>
              <div className={chartHeading}>
                <Text type="h4">{t("setup.diagnosis.failure.compare.chart.title")}</Text>
                <Text className={secondaryText} type="body2">
                  {t("setup.diagnosis.failure.compare.chart.observable_count", quantityIds.length)}
                </Text>
              </div>
              <section
                aria-label={t("setup.diagnosis.failure.compare.controls")}
                className={chartControls}
              >
                <Field
                  id="comparison-failure-sorting"
                  label={t("setup.diagnosis.failure.compare.sort.label")}
                  inputContainerClassName={chartControlField}
                >
                  <Selector
                    className={sortSelector}
                    options={sortOptions}
                    value={availableSort}
                    onChange={setSort}
                  />
                </Field>
                <Field
                  id="minimum-difference"
                  label={t("setup.diagnosis.failure.compare.minimum_difference")}
                  tooltipOverlay={
                    <>
                      <strong>
                        {t("setup.diagnosis.failure.compare.minimum_difference.summary")}
                      </strong>
                      <br />
                      <br />
                      {t("setup.diagnosis.failure.compare.minimum_difference.tooltip")}
                    </>
                  }
                  inputContainerClassName={chartControlField}
                >
                  <Slider
                    aria-labelledby={minimumDifferenceLabelId}
                    className={thresholdSlider}
                    inputPosition="before"
                    max={MAX_DIFFERENCE_PERCENTAGE}
                    min={MIN_DIFFERENCE_PERCENTAGE}
                    onChange={handleMinimumDifferenceChange}
                    showBounds={false}
                    showInitialMarker={false}
                    step={1}
                    value={minimumDifferencePercentage}
                  />
                </Field>
              </section>
              <div className={comparisonChart}>
                {quantityIds.length === 0 ? (
                  <div className={emptyChartMessage}>
                    <AdditionnalMessage
                      status="INFO"
                      text={t("setup.diagnosis.failure.compare.empty")}
                    />
                  </div>
                ) : (
                  <HighchartsComponent
                    ref={chartRef}
                    name="fault-signature-comparison"
                    options={chartOptions}
                    selectablePoints={false}
                  />
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </ModalV2>
  );
};

const comparisonContent = css`
  display: grid;
  gap: ${spacings.m};
  min-width: 0;
`;

const responsiveComparisonModal = css`
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;

  @media (min-width: 500px) {
    &.medium {
      width: min(800px, calc(100vw - 48px));
    }
  }

  @media (min-width: 1200px) {
    &.medium {
      width: min(1100px, calc(100vw - 96px));
    }
  }

  @media (min-width: 1600px) {
    &.medium {
      width: min(1200px, calc(100vw - 128px));
    }
  }
`;

const setupSection = css`
  display: flex;
  gap: ${spacings.xs};
  justify-items: start;
  align-items: center;
`;

const setupSelector = css`
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
`;

const selectorOptionLabel = css`
  display: block;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.unavailable {
    opacity: 0.5;
  }
`;

const scoresSection = css`
  display: grid;
  gap: ${spacings.xs};
  min-width: 0;
`;

const scoresHeading = css`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${spacings.xxs};
`;

const informationButton = css`
  padding: ${spacings.xxs};
`;

const scoresList = css`
  display: grid;
  row-gap: ${spacings.xxs};
  column-gap: ${spacings.m};
  grid-template-columns: repeat(3, 1fr);
`;

const scoreLabel = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const scoreValue = css`
  justify-self: end;

  &.very_similar {
    color: ${colors.oxygen400};
  }

  &.similar {
    color: ${colors.phosphorus500};
  }

  &.distinct {
    color: ${colors.chlorine600};
  }
`;

const scoreResult = css`
  display: inline-flex;
  align-items: center;
  gap: ${spacings.xs};
`;

const sectionSeparator = css`
  height: 1px;
  background-color: ${colors.titanium100};
`;

const chartControlField = css`
  border: none;
  padding: 0;

  [class*="SliderInput"] {
    > div:first-child {
      padding-left: 0;
    }
    input {
      padding: ${spacings.xxs};
    }
  }
`;

const chartControls = css`
  display: flex;
  gap: ${spacings.s};

  > * {
    flex-grow: 1;
  }
`;

const sortSelector = css`
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
`;

const sortOptionLabel = css`
  display: block;
  max-width: 145px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const thresholdSlider = css`
  max-width: 390px;
`;

const chartSection = css`
  display: grid;
  gap: ${spacings.xs};
  min-width: 0;
`;

const chartHeading = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacings.m};
`;

const secondaryText = css`
  color: ${colors.carbon700};
`;

const comparisonChart = css`
  position: relative;
  width: 100%;
  min-width: 0;
  height: 320px;
  overflow: hidden;
  padding-top: ${spacings.s};
`;

const emptyChartMessage = css`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  text-align: center;
`;
